import { useState } from 'react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Building2, MapPin, CheckCircle2, ChevronDown } from 'lucide-react';
import { TRN, Branch } from '../types';

interface TRNBranchSwitcherProps {
  trns: TRN[];
  selectedTRNId: string;
  selectedBranchId: string;
  onSwitch: (trnId: string, branchId: string) => void;
}

export default function TRNBranchSwitcher({ 
  trns, 
  selectedTRNId, 
  selectedBranchId, 
  onSwitch 
}: TRNBranchSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [tempTRNId, setTempTRNId] = useState(selectedTRNId);
  const [tempBranchId, setTempBranchId] = useState(selectedBranchId);

  const currentTRN = trns.find(t => t.id === selectedTRNId);
  const currentBranch = currentTRN?.branches.find(b => b.id === selectedBranchId);
  const tempTRN = trns.find(t => t.id === tempTRNId);
  const availableBranches = tempTRN?.branches || [];

  const handleSwitch = () => {
    if (tempTRNId && tempBranchId) {
      onSwitch(tempTRNId, tempBranchId);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className="h-10 border-gray-200 hover:border-[#0052CC] hover:bg-blue-50 transition-all"
        >
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#0052CC]" />
            <div className="text-left">
              <div className="text-sm">{currentTRN?.trnNumber}</div>
              <div className="text-xs text-gray-500">{currentBranch?.branchName}</div>
            </div>
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="p-4 border-b bg-gradient-to-r from-[#0052CC] to-indigo-600">
          <h3 className="text-white">Switch TRN & Branch</h3>
          <p className="text-xs text-blue-100 mt-1">Select a different Tax Registration Number and Branch</p>
        </div>
        
        <div className="p-4 space-y-4">
          {/* Current Selection */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-gray-600 mb-2">Currently Active</p>
            <div className="flex items-start gap-2">
              <Building2 className="w-4 h-4 text-[#0052CC] mt-0.5" />
              <div className="flex-1">
                <p className="text-sm">{currentTRN?.trnNumber}</p>
                <p className="text-xs text-gray-600">{currentTRN?.legalEntityName}</p>
                <div className="flex items-center gap-1 mt-1">
                  <MapPin className="w-3 h-3 text-gray-400" />
                  <p className="text-xs text-gray-600">{currentBranch?.branchName}</p>
                </div>
              </div>
            </div>
          </div>

          {/* TRN Selection */}
          <div className="space-y-2">
            <label className="block text-xs text-gray-700">Tax Registration Number</label>
            <Select value={tempTRNId} onValueChange={(value) => {
              setTempTRNId(value);
              setTempBranchId('');
            }}>
              <SelectTrigger className="h-10 border-gray-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {trns.map((trn) => (
                  <SelectItem key={trn.id} value={trn.id}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{trn.trnNumber}</span>
                      {trn.status === 'active' && (
                        <CheckCircle2 className="w-3 h-3 text-green-500" />
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Branch Selection */}
          <div className="space-y-2">
            <label className="block text-xs text-gray-700">Branch</label>
            <Select 
              value={tempBranchId} 
              onValueChange={setTempBranchId}
              disabled={!tempTRNId}
            >
              <SelectTrigger className="h-10 border-gray-200">
                <SelectValue placeholder={tempTRNId ? "Select Branch" : "Select TRN first"} />
              </SelectTrigger>
              <SelectContent>
                {availableBranches.map((branch) => (
                  <SelectItem 
                    key={branch.id} 
                    value={branch.id}
                    disabled={branch.status !== 'active'}
                  >
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3 h-3 text-gray-400" />
                      <span className="text-sm">{branch.branchName}</span>
                      <span className="text-xs text-gray-500">({branch.branchCode})</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setTempTRNId(selectedTRNId);
                setTempBranchId(selectedBranchId);
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-gradient-to-r from-[#0052CC] to-indigo-600 hover:from-[#0041A3] hover:to-indigo-700"
              onClick={handleSwitch}
              disabled={!tempTRNId || !tempBranchId || (tempTRNId === selectedTRNId && tempBranchId === selectedBranchId)}
            >
              Switch
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
